import React from 'react';
import CarouselProducts from '@components/CarouselProducts';
import { RichText } from 'prismic-reactjs';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  IPrismicRichText,
  ISearchProduct,
} from '@monofront/types';
import HeartLoader from '@monofront/design-system/components/HeartLoader';
import {
  ICardProductInfos,
  useMerchProductsRecommendations,
} from '@monofront/business/hooks';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import { useRecommendationTracking } from '@hooks/tracking/useRecommendationTracking';

interface IMerchRecommendationsCarouselProps {
  title: IPrismicRichText | string;
  widgetId: string;
  sku?: string;
  widgetContext?: Omit<AddToShoppingCartContext, 'source'>;
  trackingId?: string;
  isOnCardClickDisabled?: boolean;
  isProductDrawerEnabled?: boolean;
}

type InnerMerchRecommendationsCarouselProps = Omit<
  IMerchRecommendationsCarouselProps,
  'sku'
> & {
  products: Array<ISearchProduct>;
  widgetTitle?: IPrismicRichText;
  loading: boolean;
};

const MerchRecommendationsCarouselContainer: React.FC<
  IMerchRecommendationsCarouselProps
> = ({ widgetId, sku, ...props }) => {
  const { shoppingCart } = useShoppingCart();
  const {
    loading,
    products,
    title: widgetTitle,
  } = useMerchProductsRecommendations({
    widgetId,
    shoppingCart,
    sku,
  });

  return (
    <>
      <div className="slice-carousel-recommendations">
        {loading && products.length === 0 && (
          <div className="loader-container">
            <HeartLoader />
          </div>
        )}
        <MerchRecommendationsCarousel
          {...props}
          widgetId={widgetId}
          products={products}
          widgetTitle={widgetTitle}
          loading={loading}
        />
      </div>
      <style jsx>{`
        .loader-container {
          display: flex;
          align-items: center;
          height: 360px; /* value took from browser DOM inspector. not calculated */
          margin-bottom: var(--space-8);
        }

        @media (max-width: 800px) {
          .loader-container {
            height: 470px; /* value took from browser DOM inspector. not calculated */
            margin-bottom: inherit;
          }
        }
      `}</style>
    </>
  );
};

const _MerchRecommendationsCarousel: React.FC<
  InnerMerchRecommendationsCarouselProps
> = ({
  title,
  widgetContext = {},
  trackingId,
  isOnCardClickDisabled,
  isProductDrawerEnabled = false,
  products,
  widgetTitle,
  widgetId,
}) => {
  const { sendSelectItemEvent } = useRecommendationTracking({
    trackingId: widgetId,
  });

  const context = { ...widgetContext };

  if (widgetContext.widgetName) {
    context.widgetName = `IH-${widgetContext.widgetName}`; // IH prefix for "In House"
  }

  const trackProductInteraction = (
    product: Partial<ICardProductInfos>,
    index: number,
  ) => {
    if (product.sku && product.title) {
      sendSelectItemEvent({
        product: { sku: product.sku, name: product.title, index },
      });
    }
  };

  return (
    <>
      {!!products.length && (
        <CarouselProducts
          products={products}
          headerTitle={
            typeof title === 'string' ? (
              <h2 className="tw-lf-title-sm desktop:tw-lf-title-md">{title}</h2>
            ) : (
              <RichText
                render={title ?? widgetTitle}
                htmlSerializer={CustomPrismicHeading({
                  className: 'tw-lf-title-sm desktop:tw-lf-title-md',
                })}
              />
            )
          }
          containerContext={{
            source: AddToShoppingCartSource.RECOMMENDATION,
            ...context,
          }}
          trackingId={trackingId ? `IH-${trackingId}` : undefined} // IH prefix for "In House"
          isOnCardClickDisabled={isOnCardClickDisabled}
          isProductDrawerEnabled={isProductDrawerEnabled}
          trackProductInteraction={trackProductInteraction}
        />
      )}
    </>
  );
};

const MerchRecommendationsCarousel = React.memo(_MerchRecommendationsCarousel);

export default MerchRecommendationsCarouselContainer;
